import {useMemo} from 'react';
import {
	parseActions,
	type ParseActionsCommonOptions,
} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	assetViewActionEdit,
	assetViewActionDelete,
	assetViewActionDownload,
	AssetViewEvent,
} from '../actions';
import {useAssetViewsProvider} from '../contexts';
import {type IAsset} from '@netx/core-asset-next/schema';
import {type IAssetView} from '../schema';

export interface UseAssetViewActionsParams extends ParseActionsCommonOptions {
	asset?: IAsset;
	view?: IAssetView;
}
/**
 * Hook to obtain asset view actions such as add, edit, and remove.
 * It leverages the asset views provider for action implementations.
 *
 * @param {UseAssetViewActionsParams} props - Optional parameters including the asset and view objects.
 * @returns An object with functions to perform actions on the specified asset view.
 */
export const useAssetViewActions = (props: UseAssetViewActionsParams = {}) => {
	const {editView, deleteView, downloadView} = useAssetViewsProvider();

	const getActions = useConstCallback(
		({asset, view, ...props}: UseAssetViewActionsParams = {}) => {
			return !asset || !view
				? []
				: parseActions({
						actions: [
							assetViewActionEdit(asset, view, {
								cb: () => editView(view.viewId),
							}),
							assetViewActionDownload(asset, view, {
								cb: () => downloadView(view.viewId),
							}),
							assetViewActionDelete(asset, view, {
								cb: () => deleteView(view.viewId),
								group: 2,
							}),
						],
						args: [asset, view],
						...props,
					});
		},
	);

	const actions = useMemo(() => getActions(props), [props]);

	return {
		actions,
		getActions,
	};
};
